import { getRandomSeed, shallowCopy } from "@noli/core";

import { CapsulesPanel } from "@/components/Capsules";
import {
  setModelType,
  setNegativePrompt,
  setTxt2ImgArtist,
  setTxt2ImgStyle,
  setTxt2ImgTexts,
  statusStore,
} from "@/stores/status";
import { META_MODAL_NOLI_MODEL_MAP, recommendations, SD_VERSION } from "@/utils/constants";
import { Editor_Words } from "@/utils/lang/editor";
import { langStore, translate, useSelecting } from "@noli/business";
import { runInAction } from "mobx";
import React from "react";
import { tasksStore } from "@/stores/tasks";
import { toast, useOwnToast } from "@/utils/misc";
import { Actions } from "@/utils/api";
import { observer } from "mobx-react-lite";
import { isAuthUser, isVip } from "@/utils/status";
import { FreeTaskMessage } from "../../FreeTaskMessage";
import { Txt2ImgMetaData } from "@/types/meta";

const Recommendations: React.FC<{
  setCircular: (use: boolean) => void;
  setIsAnime: (isAnime: boolean) => void;
}> = ({ setCircular, setIsAnime }) => {
  const lang = langStore.tgt;
  const t = useOwnToast();

  function handleClick(word: string) {
    return async () => {
      const recommendation = recommendations.find((r) => r.word === word);
      if (!recommendation) {
        console.error(`unrecognized recommend word ${word} occurred`);
        return;
      }
      const promptInfo =
        lang === "zh" ? recommendation.data.promptInfo : recommendation.data.enPromptInfo;
      runInAction(() => {
        statusStore.txt2imgSize = recommendation.data.size;
        setCircular(recommendation.data.use_circular);
        const metaModelType =
          recommendation.data.model ?? recommendation.data.is_anime ? "anime" : "basic";
        setModelType(META_MODAL_NOLI_MODEL_MAP[metaModelType]);
        setIsAnime(recommendation.data.is_anime);
        setTxt2ImgTexts(promptInfo.original);
        setTxt2ImgStyle(promptInfo.style ?? "none-style");
        setTxt2ImgArtist(promptInfo.artist ?? "none-artist");
        setNegativePrompt(promptInfo.originalNegative ?? "");
      });
      // call api
      const busyAlias = ".onRecommend.";
      const { w: txt2imgW, h: txt2imgH } = statusStore.getTxt2imgGenerateSize(null);
      // sanity check
      if (!isVip() && tasksStore.busyNodes.has(busyAlias)) {
        if (!isAuthUser()) {
          toast(t, "info", translate(Editor_Words["already-in-queue-message"], lang));
          return;
        } else {
          toast(t, "info", <FreeTaskMessage />);
          return;
        }
      }

      const metaData: Txt2ImgMetaData = {
        w: txt2imgW,
        h: txt2imgH,
        text: "",
        use_circular: recommendation.data.use_circular,
        is_anime: recommendation.data.is_anime,
        num_steps: recommendation.data?.num_steps,
        guidance_scale: recommendation.data?.guidance_scale,
        seed: getRandomSeed(),
        model: recommendation.data.model ?? (recommendation.data.is_anime ? "anime" : "basic"),
        promptInfo: shallowCopy(promptInfo),
      };
      await Actions.importMeta({
        t,
        lang,
        type: "txt2img",
        meta: {
          type: "txt2img",
          data: metaData,
        },
        setupInfo: { checkBusy: false, busyAliases: [busyAlias] },
        aliasPrefix: "txt2img.recommend",
        opt: { freezePrompt: false },
      });
    };
  }

  return (
    <CapsulesPanel
      mt="-16px"
      mb="8px"
      innerMT="-4px"
      h="135px"
      noSeparator
      words={recommendations.map(({ word }) => word)}
      callbackFn={handleClick}
      panelTitle={translate(Editor_Words["txt2img-sd-recommend-title"], lang)}
    />
  );
};

export const GenerateRecommendations = observer(Recommendations);
